import { createContext, useContext, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';
import { getUserInfo, revokeToken, refreshAccessToken } from '../api/auth';
import { clearAuthData } from '../services/tokenStorage';
import { UserProfile } from '../../generated/auth/v1/auth_pb';

// Refresh token 2 minutes before expiry
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

interface AuthContextValue {
  // From store
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;

  // Actions
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    accessToken,
    refreshToken,
    tokenExpiresAt,
    isAuthenticated,
    isLoading,
    isInitializing,
    isHydrated,
    error,
    setUser,
    setTokens,
    clearAuth,
    setInitializing,
    isTokenExpired,
  } = useAuthStore();

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Schedule a token refresh before expiry
  const scheduleRefresh = useCallback((expiresAt: number, currentRefreshToken: string) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const timeUntilRefresh = expiresAt - Date.now() - REFRESH_BUFFER_MS;

    // If already past refresh time, refresh immediately
    if (timeUntilRefresh <= 0) {
      performRefresh(currentRefreshToken);
      return;
    }

    // Schedule refresh
    refreshTimerRef.current = setTimeout(() => {
      performRefresh(currentRefreshToken);
    }, timeUntilRefresh);
  }, []);

  // Perform token refresh
  const performRefresh = useCallback(async (currentRefreshToken: string) => {
    try {
      const { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn } =
        await refreshAccessToken(currentRefreshToken);

      setTokens(newAccessToken, newRefreshToken, expiresIn);

      // Schedule the next refresh
      const newExpiresAt = Date.now() + Number(expiresIn) * 1000;
      scheduleRefresh(newExpiresAt, newRefreshToken);
    } catch (error) {
      console.error('Background token refresh failed:', error);
      clearAuth();
      clearAuthData();
    }
  }, [setTokens, clearAuth, scheduleRefresh]);

  // Initialize auth after store rehydration
  useEffect(() => {
    // Wait for Zustand persist to load from localStorage
    if (!isHydrated) {
      return;
    }

    const initAuth = async () => {
      setInitializing(true);

      try {
        // No tokens at all - user needs to log in
        if (!accessToken && !refreshToken) {
          clearAuth();
          clearAuthData();
          return;
        }

        // Access token expired but we have a refresh token - try to refresh
        if ((!accessToken || isTokenExpired()) && refreshToken) {
          try {
            const { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn } =
              await refreshAccessToken(refreshToken);

            setTokens(newAccessToken, newRefreshToken, expiresIn);

            // Verify the new token by fetching user info
            const userInfo = await getUserInfo(newAccessToken);
            setUser(userInfo);

            // Schedule background refresh
            const expiresAt = Date.now() + Number(expiresIn) * 1000;
            scheduleRefresh(expiresAt, newRefreshToken);
          } catch (error) {
            console.error('Token refresh failed during init:', error);
            clearAuth();
            clearAuthData();
          }
          return;
        }

        // We have a valid access token - verify it and set up refresh
        if (accessToken && !isTokenExpired()) {
          try {
            const userInfo = await getUserInfo(accessToken);
            setUser(userInfo);

            // Schedule background refresh if we have expiry info
            if (tokenExpiresAt && refreshToken) {
              scheduleRefresh(tokenExpiresAt, refreshToken);
            }
          } catch (error) {
            // Token validation failed - try refresh if we have a refresh token
            if (refreshToken) {
              try {
                const { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn } =
                  await refreshAccessToken(refreshToken);

                setTokens(newAccessToken, newRefreshToken, expiresIn);

                const userInfo = await getUserInfo(newAccessToken);
                setUser(userInfo);

                const expiresAt = Date.now() + Number(expiresIn) * 1000;
                scheduleRefresh(expiresAt, newRefreshToken);
              } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                clearAuth();
                clearAuthData();
              }
            } else {
              console.error('Token validation failed:', error);
              clearAuth();
              clearAuthData();
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuth();
        clearAuthData();
      } finally {
        setInitializing(false);
      }
    };

    initAuth();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isHydrated]); // Run when store finishes loading from localStorage

  // Re-schedule refresh when tokens change (e.g., after login or manual refresh)
  useEffect(() => {
    if (isAuthenticated && tokenExpiresAt && refreshToken && isHydrated && !isInitializing) {
      scheduleRefresh(tokenExpiresAt, refreshToken);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isAuthenticated, tokenExpiresAt, refreshToken, isHydrated, isInitializing, scheduleRefresh]);

  const logout = useCallback(async () => {
    try {
      // Revoke refresh token on backend (best effort)
      if (refreshToken) {
        await revokeToken(refreshToken);
      }
    } catch (error) {
      console.error('Token revocation failed:', error);
      // Continue with logout even if revocation fails
    } finally {
      // Clear local state
      clearAuth();
      clearAuthData();

      // Redirect to login page
      window.location.href = '/login';
    }
  }, [refreshToken, clearAuth]);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (!accessToken || isTokenExpired()) {
      clearAuth();
      clearAuthData();
      return false;
    }
    return true;
  }, [accessToken, isTokenExpired, clearAuth]);

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    isInitializing,
    error,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
